//#region react import
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
//#endregion

//#region function import
import useClickOutside from "../../Helper/CustomHook/useClickOutside";
import { SetDataTestId } from "../../Helper/DataTestId";
//#endregion

//#region component import
import AcreosCheckBox from "../AcreosCheckBox/AcreosCheckBox.component";
//#endregion

//#region helper import
import { getTranslations } from "../../Helper/TranslationController";
//#endregion

//#region constants import
import { ICON_CLOSE, ICON_KEYBOARD_ARROW_RIGHT } from "../../Constants/IconConstants";
import { TRANSLATIONS } from "../../Constants/Translations";
//#endregion

//#region style import
import "./MultiSelect.style.scss";
//#endregion

const MultiSelect = ({
  title,
  iconCustom,
  id,
  value = [],
  setNewValue,
  defaultText,
  options = [],
  clearable,
  addClassCustom = "",
  OptionComponent,
  dataTestId,
  isValid = true,
  errorMessage,
  scrollTarget,
  showSelectedCount = true,
  selectAllEnabled = false,
}) => {
  //#region hooks
  const translations = useSelector((s) => s.translationSlice.translations);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [internal, setInternal] = useState(() => new Set(value?.map(String)));
  const opts = useRef(null);
  const cross = useRef(null);
  const inputRef = useRef(null);
  //#endregion

  //#region useMemos
  const selectedOptions = useMemo(
    () => options.filter((option) => internal.has(String(option.value))),
    [options, internal]
  );

  const labelToShow = useMemo(() => {
    if (!selectedOptions.length) return defaultText;
    if (OptionComponent)
      return showSelectedCount
        ? `${selectedOptions.length} ${getTranslations(TRANSLATIONS.SELECTED_TRAININGS, translations)}`
        : defaultText;
    const labels = selectedOptions.map((option) => option.label);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
  }, [selectedOptions, defaultText, OptionComponent, showSelectedCount]);
  //#endregion

  //#region constants
  const openOptions = (event) => {
    if (event.target !== cross.current) setOptionsOpen((prev) => !prev);
  };

  const closeOptions = () => setOptionsOpen(false);

  const toggleOption = (option, event) => {
    event.stopPropagation();
    const next = new Set(internal);
    const key = String(option.value);
    next.has(key) ? next.delete(key) : next.add(key);
    setInternal(next);
    setNewValue(Array.from(next));
  };

  const clearSelect = (event) => {
    event?.stopPropagation();
    setInternal(new Set());
    setNewValue([]);
    setOptionsOpen(false);
  };

  const handleSelectAll = (event) => {
    event?.stopPropagation();
    const allEnabled = options.filter((o) => !o.disabled);
    if (selectedOptions.length === allEnabled.length) {
      setInternal(new Set());
      setNewValue([]);
    } else {
      const all = new Set(allEnabled.map((o) => String(o.value)));
      setInternal(all);
      setNewValue(Array.from(all));
    }
  };
  //#endregion

  //#region useEffects
  useEffect(() => {
    if (scrollTarget && scrollTarget?.id === id) {
      inputRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
  }, [scrollTarget, id]);

  useEffect(() => {
    setInternal(new Set((value || []).map(String)));
  }, [value]);

  useClickOutside([opts, inputRef], optionsOpen, closeOptions);
  //#endregion

  return (
    <section
      className={`acreos-select ${addClassCustom}`}
      data-testid={SetDataTestId(dataTestId)}
    >
      <div>
        {title && (
          <label>
            <b>{title}</b>
          </label>
        )}

        <div
          className={`acreos-select_selected ${!isValid ? "invalid" : optionsOpen ? "selected" : ""}`}
          data-testid={SetDataTestId(`${dataTestId}_selected`)}
          onClick={openOptions}
          ref={inputRef}
        >
          <span
            className={`acreos-select_default ${!selectedOptions.length ? "error" : ""}`}
            data-testid={SetDataTestId(`${dataTestId}_selected_label`)}
          >
            {labelToShow}
          </span>

          <div className="icons">
            <i
              className={iconCustom || ICON_KEYBOARD_ARROW_RIGHT}
              style={{ position: "relative", transform: optionsOpen ? "rotate(-90deg)" : "rotate(90deg)" }}
            />
            {clearable && (
              <i
                ref={cross}
                className={`acreos-select_clearable ${ICON_CLOSE}`}
                style={{ position: "relative" }}
                onClick={clearSelect}
                data-testid={SetDataTestId(`${dataTestId}_clearable`)}
              />
            )}
          </div>

          <div
            ref={opts}
            className={(optionsOpen ? "" : "hide ") + "acreos-select_options"}
            onClick={(e) => e.stopPropagation()}
            data-testid={SetDataTestId(`${dataTestId}_options`)}
          >
            {selectAllEnabled && (
              <div
                className="acreos-multiselect_option"
                onClick={handleSelectAll}
              >
                <AcreosCheckBox
                  checked={selectedOptions.length === options.filter((option) => !option.disabled).length}
                  onChange={() => handleSelectAll({ stopPropagation: () => {} })}
                />
                <span className="acreos-select_option_label">
                  {selectedOptions.length === options.filter((option) => !option.disabled).length
                    ? getTranslations(TRANSLATIONS.DESELECT_ALL, translations)
                    : getTranslations(TRANSLATIONS.SELECT_ALL, translations)}
                </span>
              </div>
            )}

            {options.map(
              (option, key) =>
                !option.disabled && (
                  <div
                    key={key}
                    value={option.value}
                    className="acreos-multiselect_option"
                    onClick={(event) => toggleOption(option, event)}
                  >
                    <AcreosCheckBox checked={internal.has(String(option.value))} />
                    {OptionComponent ? (
                      <OptionComponent
                        value={option}
                        isChecked={internal.has(String(option.value))}
                        overrideClass={`elipsis ${
                          internal.has(String(option.value)) ? "color-primary" : "color-text-primary"
                        }`}
                      />
                    ) : (
                      <span
                        className={`acreos-select_option_label ${
                          internal.has(String(option.value)) ? "color-primary" : ""
                        }`}
                      >
                        {option.label}
                      </span>
                    )}
                  </div>
                )
            )}
          </div>
        </div>
      </div>

      {errorMessage && <span className="acreos-select_error-message">{!isValid && errorMessage}</span>}
    </section>
  );
};

export default MultiSelect;
